/**
 * Pure helpers for deriving display metadata from a viewer `Layer`.
 *
 * Used by `SceneTreePanel` to populate per-card chip rows (CRS, bbox area,
 * source artifact, feature count, point budget, etc.). Kept side-effect
 * free so the same helpers can power the SceneTreePanel and any future
 * "Layer info" dialog without re-implementing the parsing.
 */
import type { Layer } from '@geosoft/viewer-sdk';

export type ChipTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface MetadataChip {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly tone: ChipTone;
}

/** Digit-grouped integer ("1,234"). */
export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

/** Compact short-form count: 1.2K, 3.4M, 5.6B. */
export function formatCompactCount(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return `${Math.round(value)}`;
}

/** "1.2 MB", "342 KB", "1.5 GB". */
export function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  const decimals = amount >= 10 || unit === 0 ? 0 : 1;
  return `${amount.toFixed(decimals)} ${units[unit]}`;
}

/** Square-meters, formatted with km² for big areas. */
export function formatArea(squareMeters: number): string {
  if (!Number.isFinite(squareMeters) || squareMeters <= 0) return '—';
  if (squareMeters >= 1_000_000) {
    return `${(squareMeters / 1_000_000).toFixed(2)} km²`;
  }
  if (squareMeters >= 10_000) {
    return `${(squareMeters / 10_000).toFixed(2)} ha`;
  }
  return `${formatNumber(squareMeters)} m²`;
}

const EARTH_RADIUS_M = 6_378_137; // WGS84 semi-major axis (sphere approx).

/**
 * Approximate the area of a bbox in square meters. Accepts either
 * EPSG:4326 (lon/lat degrees) or EPSG:3857 (meters), heuristically:
 * if all coords are within ±200, treat them as degrees and use a
 * spherical-cap approximation; otherwise treat them as meters and
 * use planar width × height. Returns `0` for invalid / global bboxes.
 */
export function bboxAreaSquareMeters(
  bbox: readonly number[] | null | undefined,
): number {
  if (!bbox || bbox.length < 4) return 0;
  const [minX, minY, maxX, maxY] = bbox;
  if (
    !Number.isFinite(minX)
    || !Number.isFinite(minY)
    || !Number.isFinite(maxX)
    || !Number.isFinite(maxY)
  ) {
    return 0;
  }
  if (maxX <= minX || maxY <= minY) return 0;

  const looksLikeDegrees = Math.abs(minX) <= 200 && Math.abs(maxX) <= 200
    && Math.abs(minY) <= 100 && Math.abs(maxY) <= 100;

  // Treat near-global bboxes as "no useful area to report".
  if (looksLikeDegrees) {
    const lonSpanDeg = maxX - minX;
    const latSpanDeg = maxY - minY;
    if (lonSpanDeg >= 359 || latSpanDeg >= 169) return 0;
    const midLatRad = ((maxY + minY) / 2) * (Math.PI / 180);
    const metersPerLatDeg = (Math.PI / 180) * EARTH_RADIUS_M;
    const metersPerLonDeg = metersPerLatDeg * Math.cos(midLatRad);
    return Math.abs(lonSpanDeg * metersPerLonDeg * latSpanDeg * metersPerLatDeg);
  }
  // Web Mercator meters — guard against the world-extent default.
  const worldHalf = 20037508;
  if (Math.abs(minX) >= worldHalf - 1 && Math.abs(maxX) >= worldHalf - 1) return 0;
  return (maxX - minX) * (maxY - minY);
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Best-effort filename extraction from a path/URL. Returns the last
 * non-empty segment after stripping query/hash.
 */
export function basename(pathOrUrl: string | null | undefined): string | null {
  if (!pathOrUrl) return null;
  const cleaned = pathOrUrl.split(/[?#]/)[0];
  const segments = cleaned.split(/[\\/]+/).filter((seg) => seg.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : null;
}

/**
 * Build the metadata chip row for a layer. Returns an ordered list of
 * chips that should appear left-to-right under the layer title.
 */
export function metadataChipsFor(layer: Layer): MetadataChip[] {
  const chips: MetadataChip[] = [];
  const style = readRecord(layer.style) ?? {};

  // CRS (always show when present)
  if (typeof layer.crs === 'string' && layer.crs.length > 0) {
    chips.push({ key: 'crs', label: 'CRS', value: layer.crs, tone: 'neutral' });
  }

  // Area
  const area = bboxAreaSquareMeters(layer.bbox);
  if (area > 0) {
    chips.push({ key: 'area', label: 'Area', value: formatArea(area), tone: 'neutral' });
  }

  // Per-kind specifics
  if (layer.kind === 'raster' || layer.kind === 'terrain') {
    const sourceFile = basename(readString(style['sourceArtifact']) ?? readString(layer.endpoints.tiles));
    if (sourceFile) {
      chips.push({ key: 'source', label: 'Source', value: sourceFile, tone: 'info' });
    }
    const size = readNumber(style['sourceSize']);
    if (size) {
      chips.push({ key: 'size', label: 'Size', value: formatBytes(size), tone: 'neutral' });
    }
  }

  if (layer.kind === 'vector') {
    const featureCount = readNumber(style['featureCount']);
    if (featureCount !== null) {
      chips.push({
        key: 'features',
        label: 'Features',
        value: formatCompactCount(featureCount),
        tone: 'info',
      });
    }
    const geomType = readString(style['geometryType']);
    if (geomType) {
      chips.push({ key: 'geom', label: 'Geom', value: geomType, tone: 'neutral' });
    }
    const sourceLayer = readString(style['sourceLayer']);
    if (sourceLayer) {
      chips.push({ key: 'sl', label: 'Source layer', value: sourceLayer, tone: 'neutral' });
    }
  }

  if (layer.kind === 'pointcloud') {
    const pcRecord = readRecord(style['pointcloud']);
    const totalPoints = readNumber(pcRecord?.['pointCount'])
      ?? readNumber(readRecord(pcRecord?.['pointBudget'])?.['totalPoints']);
    if (totalPoints !== null) {
      chips.push({
        key: 'points',
        label: 'Points',
        value: `${formatCompactCount(totalPoints)} pts`,
        tone: 'info',
      });
    }
  }

  if (layer.kind === 'bim' || layer.kind === '3dtiles') {
    const triangles = readNumber(style['triangleCount']) ?? readNumber(style['triangles']);
    if (triangles !== null) {
      chips.push({
        key: 'tris',
        label: 'Triangles',
        value: `${formatCompactCount(triangles)} tri`,
        tone: 'info',
      });
    }
  }

  // Converter run id (mono-style chip at end)
  const runId = readString(style['converterRunId']);
  if (runId) {
    chips.push({ key: 'run', label: 'Run', value: runId, tone: 'neutral' });
  }

  return chips;
}

// ---------------------------------------------------------------------------
// Slot grouping (C1)
// ---------------------------------------------------------------------------

export type SceneSlotName =
  | 'basemaps'
  | 'terrain'
  | 'realityData'
  | 'engineeringModels'
  | 'featureLayers'
  | 'overlays'
  | 'environment';

export const SLOT_ORDER: readonly SceneSlotName[] = [
  'basemaps',
  'terrain',
  'realityData',
  'engineeringModels',
  'featureLayers',
  'overlays',
  'environment',
];

export const SLOT_LABEL: Readonly<Record<SceneSlotName, string>> = {
  basemaps: 'Basemaps',
  terrain: 'Terrain',
  realityData: 'Reality data',
  engineeringModels: 'Engineering models',
  featureLayers: 'Feature layers',
  overlays: 'Overlays',
  environment: 'Environment',
};

export function isSceneSlot(value: unknown): value is SceneSlotName {
  return typeof value === 'string' && (SLOT_ORDER as readonly string[]).includes(value);
}
